import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { ExpansionService } from '../../expansion.service';
import { ExpanderComponent } from '../expander/expander.component';
import { ExpansionInstance } from '../../types/expansion-instance';
import { ActivatedRoute, Router } from '@angular/router';

declare const document: any;

@Component({
	selector: 'ws-tab-expander',
	templateUrl: './tab-expander.component.html',
	styleUrls: ['./tab-expander.component.scss'],
})
export class TabExpanderComponent<
	COMPONENT_INTERFACE extends Record<string, any> = Record<string, any>
> extends ExpanderComponent {
	@Input() useRouteHash = false;
	@Input() tabNavigationPlacement: 'outside' | 'inside' = 'outside';
	@Input() disabled = false;

	public activeTab?: string;

	private _tabFlags = new Map<string, Set<string>>();

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		expansionService: ExpansionService,
		cdr: ChangeDetectorRef
	) {
		super(expansionService, cdr);
	}

	public get refs() {
		return this.cluster?.instances.filter((i) => !i?.ref?.instance?.['hideTab']?.());
	}

	public setActive(id: string | null) {
		if (!id || !this.refs.find((ini) => ini.id === id)) {
			return; // attempt to set tab that does not exist
		}

		const _this = this;

		const previousActive = this.refs.find((ini) => ini.id === _this.activeTab);

		// Wrap DOM changes in View Transition for smooth width animation
		const updateDOM = () => {
			// @ts-ignore
			this.removeCssClassOnClusterInstance(previousActive, 'active');

			this.activeTab = id;
			if (this.useRouteHash) {
				this.setFragment(this.activeTab);
			}

			const newActive = this.refs.find((ini) => ini.id === id);
			// @ts-ignore
			this.setCssClassOnClusterInstance(newActive, 'active');

			// Trigger change detection to update tab underline immediately
			this.cdr.detectChanges();
		};

		// Use View Transitions API if available, otherwise fallback to direct update
		if ((document as any).startViewTransition) {
			const transition = (document as any).startViewTransition(() => updateDOM());
			// Ensure change detection runs after transition completes
			transition.finished.then(() => {
				this.cdr.detectChanges();
			});
		} else {
			updateDOM();
		}
	}

	ngAfterViewInit() {
		super.ngAfterViewInit();
		if (this.useRouteHash) {
			// Subscribe to the route to set the tab indicated by the # fragment in the URL, if present
			const sub = this.route.fragment.subscribe((fragment) => {
				this.setActive(fragment);
				//sub.unsubscribe();
			});
		}
	}

	public setTabFlag(tabId: string, flag: string): void {
		if (!this._tabFlags.has(tabId)) {
			this._tabFlags.set(tabId, new Set());
		}
		this._tabFlags.get(tabId)!.add(flag);
	}

	public removeTabFlag(tabId: string, flag: string): void {
		this._tabFlags.get(tabId)?.delete(flag);
	}

	public clearFlag(flag: string): void {
		for (const [, flags] of this._tabFlags) {
			flags.delete(flag);
		}
	}

	public hasFlag(tabId: string, flag: string): boolean {
		return this._tabFlags.get(tabId)?.has(flag) === true;
	}

	protected initCluster() {
		if (!this.clusterInitialized) {
			if (!this.styleClass) {
				this.styleClass = 'tab-expansion';
			}

			super.initCluster();

			if (!!this.cluster.instances.length) {
				this.activeTab = this.cluster.instances[0].id;
				// @ts-ignore
				this.setCssClassOnClusterInstance(this.cluster.instances[0], 'active');
			}

			this.cdr.detectChanges();
		}
	}

	protected setCssClassOnClusterInstance(instance: ExpansionInstance<COMPONENT_INTERFACE>, cssClass: string) {
		// @ts-ignore
		instance.ref?.hostView?.rootNodes[0].classList.add(cssClass);
	}

	protected removeCssClassOnClusterInstance(instance: ExpansionInstance<COMPONENT_INTERFACE>, cssClass: string) {
		// @ts-ignore
		instance.ref?.hostView?.rootNodes[0].classList.remove(cssClass);
	}

	private setFragment(tabId: string) {
		// Set # fragment in the URL with the id of the current tab to make it recoverable
		this.router.navigate([], {
			fragment: tabId,
			replaceUrl: true,
			queryParamsHandling: 'preserve',
		});
	}
}
